import React, {
  PropsWithChildren,
  useCallback,
  useEffect,
  useState,
  useContext,
  useMemo,
  useId
} from 'react';
import Surface from './Surface';
import { makeCSS } from '../../utils/makeCSS';
import { SwipeableDrawer } from '@material-ui/core';
import { Scrollbar } from '../../core/atoms/Scrollbar';
import { useModalContainer } from '../../../../ui/src/Modal/ModalManagerUI';

interface MobileSlideSurfaceProps extends PropsWithChildren {
  onClose?: () => void;
  closeOnClick?: boolean;
}

const useCSS = makeCSS(({ theme }) => {
  return {
    paper: {
      borderTopLeftRadius: theme.spacing[4],
      borderTopRightRadius: theme.spacing[4],
      overflowX: 'hidden',
      background: `linear-gradient(0deg, ${theme.color.overlay['02']} 0%, ${theme.color.overlay['02']} 100%), ${theme.color.background.neutral}`
    },
    root: {
      display: 'flex',
      flexDirection: 'column',
      justifyItems: 'center',
      alignItems: 'center',
      padding: '8px 16px',
      width: 'fit-container',
      maxHeight: '80vh',
      height: 'fit-content',
      gap: theme.spacing[4],
      background: `linear-gradient(0deg, ${theme.color.overlay['02']} 0%, ${theme.color.overlay['02']} 100%), ${theme.color.background.neutral}`
    },
    drager: {
      display: 'flex',
      width: theme.spacing[12],
      height: theme.spacing[1],
      borderRadius: theme.borderRadius.full,
      background: 'rgba(255, 255, 255, 0.20)'
    },
    children: {
      width: '100%'
    }
  };
});

interface Context {
  menuIds: string[];
  setMenuIds(val: string[] | ((ids: string[]) => string[])): void;
}

export const MobileSliderContext = React.createContext<Context | null>(null);

export const MobileSliderProvider = (props) => {
  const { children } = props;

  const [menuIds, setMenuIds] = useState([]);

  const value = useMemo(() => {
    return { menuIds, setMenuIds };
  }, [menuIds, setMenuIds]);

  return (
    <MobileSliderContext.Provider value={value}>
      {children}
    </MobileSliderContext.Provider>
  );
};

export const useRegisterMobileSurface = (prefix: string = 'mss') => {
  const uctx = useContext(MobileSliderContext);

  const id = prefix + useId();

  useEffect(() => {
    console.info('DEBUG mobileSliderContext setMenuId', id);
    uctx.setMenuIds((ids) => [...ids, id]);
    return () => {
      console.info('DEBUG mobileSliderContext setMenuId clear', id);

      uctx.setMenuIds((ids) => ids.filter((i) => i !== id));
    };
  }, []);

  return { isTopmost: uctx.menuIds.indexOf(id) === uctx.menuIds.length - 1 };
};

export const MobileSlideSurface = (props: MobileSlideSurfaceProps) => {
  const { children, onClose, closeOnClick = true } = props;
  const classes = useCSS();

  const [open, setOpen] = useState(true);

  const handleOpenDrawer = useCallback(() => {
    setOpen(true);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    if (open) {
      onClose?.();
      setOpen(false);
    }
  }, [open, onClose]);

  const { isTopmost } = useRegisterMobileSurface();

  const container = useModalContainer();

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      classes={{ paper: classes.paper }}
      onClose={handleCloseDrawer}
      onOpen={handleOpenDrawer}
      disableSwipeToOpen
      disableDiscovery
      container={container}
    >
      <Surface className={`${classes.root}`} background={'transparent'}>
        <div className={classes.drager} />

        <div
          className={classes.children}
          onTouchStart={(e) => {
            !isTopmost ? '' : e.stopPropagation();
          }}
          onTouchMove={(e) => {
            !isTopmost ? '' : e.stopPropagation();
          }}
          onClick={(e) => {
            closeOnClick ? handleCloseDrawer() : '';
          }}
        >
          <Scrollbar
            colorScrollbar="dark"
            width="100%"
            autoHeight
            autoHeightMax={'75vh'}
          >
            {children}
          </Scrollbar>
        </div>
      </Surface>
    </SwipeableDrawer>
  );
};
